import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Category, Production, PublicationStatus } from '../types/production';

export const CATEGORY_LABELS: Record<Category, string> = {
  pelicula: 'Película',
  cortometraje: 'Cortometraje',
  documental: 'Documental',
  videoclip: 'Videoclip',
  experimental: 'Experimental',
};

export const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS) as [Category, string][];

export const STATUS_LABELS: Record<PublicationStatus, string> = {
  published: 'Publicado',
  draft: 'Borrador',
  scheduled: 'Programado',
};

export function formatDuration(minutes: number | null): string | null {
  if (!minutes || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

function parseDate(iso: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatReleaseDate(iso: string | null): string | null {
  const date = parseDate(iso);
  return date ? format(date, "d 'de' MMMM 'de' yyyy", { locale: es }) : null;
}

export function formatShortDate(iso: string | null): string | null {
  const date = parseDate(iso);
  return date ? format(date, 'd MMM yyyy', { locale: es }) : null;
}

export function isUpcoming(p: Production, now: Date = new Date()): boolean {
  const date = parseDate(p.releaseDate);
  return p.status === 'scheduled' && !!date && date > now;
}

export function isLive(p: Production, now: Date = new Date()): boolean {
  if (p.status === 'published') return true;
  const date = parseDate(p.releaseDate);
  return p.status === 'scheduled' && !!date && date <= now;
}

export function getMetaParts(
  p: Pick<Production, 'year' | 'category' | 'durationMinutes'>,
): string[] {
  const parts: string[] = [];
  if (p.year) parts.push(String(p.year));
  if (p.category) parts.push(CATEGORY_LABELS[p.category]);
  const duration = formatDuration(p.durationMinutes);
  if (duration) parts.push(duration);
  return parts;
}

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function normalizeForSearch(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

export function isHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function hasPlayableSource(url: string | null): url is string {
  return isHttpUrl(url);
}

export function looksLikeDirectVideo(url: string): boolean {
  return /\.(mp4|webm|m4v|mov|ogv|ogg)(\?|#|$)/i.test(url);
}
